import type { PaginatedResponse } from "@models/common";
import type {
  ModelResponse,
  ModelViewModel,
  CostInfoResponse,
  CostInfoViewModel,
  ModelBasicInfoResponse,
  ModelBasicInfoViewModel,
  CreateModelRequest,
  UpdateModelRequest,
  CreateModelApiRequest,
  UpdateModelApiRequest,
} from "@models/model";

/**
 * Transform cost info response to view model
 */
export const buildCostInfoViewModel = (
  response: CostInfoResponse,
): CostInfoViewModel => {
  return {
    costConfig: response.cost_config,
    currency: response.currency,
    effectiveFrom: new Date(response.effective_from),
  };
};

/**
 * Transform camelCase CreateModelRequest → snake_case CreateModelApiRequest
 */
export const buildCreateModelApiRequest = (
  request: CreateModelRequest,
): CreateModelApiRequest => ({
  provider_id: request.providerId,
  model_name: request.modelName,
  display_name: request.displayName,
  status: request.status,
  model_metadata: request.modelMetadata,
  cost_info: request.costInfo
    ? {
        currency: request.costInfo.currency,
        effective_from: request.costInfo.effectiveFrom,
        cost_config: Object.fromEntries(
          Object.entries(request.costInfo.costConfig).map(([key, cfg]) => [
            key,
            {
              unit_cost: cfg.unitCost,
              unit_name: cfg.unitName,
              unit_scale: cfg.unitScale,
              tier_start: cfg.tierStart,
              tier_end: cfg.tierEnd,
            },
          ]),
        ),
      }
    : undefined,
});

/**
 * Transform camelCase UpdateModelRequest → snake_case UpdateModelApiRequest
 */
export const buildUpdateModelApiRequest = (
  request: UpdateModelRequest,
): UpdateModelApiRequest => ({
  display_name: request.displayName,
  status: request.status,
  model_metadata: request.modelMetadata,
  cost_info: request.costInfo
    ? {
        currency: request.costInfo.currency,
        effective_from: request.costInfo.effectiveFrom,
        cost_config: Object.fromEntries(
          Object.entries(request.costInfo.costConfig).map(([key, cfg]) => [
            key,
            {
              unit_cost: cfg.unitCost,
              unit_name: cfg.unitName,
              unit_scale: cfg.unitScale,
              tier_start: cfg.tierStart,
              tier_end: cfg.tierEnd,
            },
          ]),
        ),
      }
    : undefined,
});

/**
 * Transform a single model response to view model
 */
export const buildModelViewModel = (
  response: ModelResponse,
): ModelViewModel => {
  return {
    id: response.id,
    providerId: response.provider_id,
    modelName: response.model_name,
    displayName: response.display_name,
    status: response.status,
    modelMetadata: response.model_metadata,
    costInfo: response.cost_info
      ? buildCostInfoViewModel(response.cost_info)
      : null,
    createdAt: new Date(response.created_at),
  };
};

/**
 * Transform paginated model response to view model
 */
export const buildPaginatedModelsViewModel = (
  response: PaginatedResponse<ModelResponse>,
): PaginatedResponse<ModelViewModel> => {
  return {
    ...response,
    items: response.items.map(buildModelViewModel),
  };
};

/**
 * Transform a single model basic info response to view model
 */
export const buildModelBasicInfoViewModel = (
  response: ModelBasicInfoResponse,
): ModelBasicInfoViewModel => {
  return {
    id: response.id,
    modelName: response.model_name,
    displayName: response.display_name,
  };
};

/**
 * Transform array of model basic info responses to view models
 */
export const buildModelsBasicInfoViewModel = (
  responses: ModelBasicInfoResponse[],
): ModelBasicInfoViewModel[] => {
  return responses.map(buildModelBasicInfoViewModel);
};
